import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Wallet } from 'lucide-react';
import { getOnlinePayments, setOnlinePayments } from '@/lib/api/admin';
import { describeError } from '@/lib/api/client';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog } from '@/components/ui/dialog';
import { QueryError, Skeleton } from '@/components/ui/feedback';
import { Switch } from '@/components/ui/switch';
import { toast } from '@/components/ui/toast';

/**
 * The platform switch for online payments (Montra). A flip changes what every
 * hotel can do, so it always goes through a confirmation that says exactly
 * what happens on the other side.
 */
export function OnlinePaymentsCard() {
  const qc = useQueryClient();
  const status = useQuery({ queryKey: ['admin', 'online-payments'], queryFn: getOnlinePayments });
  const [pending, setPending] = useState<boolean | null>(null);

  const flip = useMutation({
    mutationFn: setOnlinePayments,
    onSuccess: (res) => {
      toast('success', res.enabled ? 'Онлайн-оплата включена' : 'Онлайн-оплата выключена');
      qc.setQueryData(['admin', 'online-payments'], res);
      setPending(null);
    },
    onError: (err) => toast('error', 'Не удалось переключить', describeError(err)),
  });

  const enabled = status.data?.enabled;

  return (
    <Card className="mb-4">
      <div className="flex flex-wrap items-start gap-4">
        <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-accent/10 text-accent">
          <Wallet size={20} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-sm font-bold text-ink">Онлайн-оплата (Montra)</h2>
            {enabled !== undefined && (
              <Badge dot tone={enabled ? 'ok' : 'warn'}>{enabled ? 'Включена' : 'Выключена'}</Badge>
            )}
          </div>
          <p className="mt-1 max-w-2xl text-xs leading-5 text-muted">
            Выключенная: у отелей нет онлайн-оплаты и кнопок «Оплатить», ссылку на онлайн-оплату гостю
            выставить нельзя, автопродления и письма о неоплате остановлены. Сроки подписок при этом
            действуют — продлевайте вручную в карточке организации.
          </p>
        </div>
        <div className="pt-1">
          {status.isPending
            ? <Skeleton className="h-[22px] w-10" />
            : status.isError ? null : (
              <Switch
                checked={!!enabled}
                disabled={status.isError || flip.isPending}
                label="Онлайн-оплата"
                onChange={(next) => setPending(next)}
              />
            )}
        </div>
      </div>

      {status.isError && (
        <div className="mt-3">
          <QueryError error={status.error} onRetry={() => status.refetch()} />
        </div>
      )}

      <Dialog
        open={pending !== null}
        onClose={() => setPending(null)}
        title={pending ? 'Включить онлайн-оплату?' : 'Выключить онлайн-оплату?'}
        footer={(
          <>
            <Button variant="ghost" onClick={() => setPending(null)}>Отмена</Button>
            <Button loading={flip.isPending} onClick={() => pending !== null && flip.mutate(pending)}>
              {pending ? 'Включить' : 'Выключить'}
            </Button>
          </>
        )}
      >
        <div className="space-y-2 px-5 py-4 text-sm leading-6">
          {pending ? (
            <>
              <p>Отели снова увидят кнопки «Оплатить» и смогут платить через Montra. Убедитесь, что шлюз принимает платежи — иначе каждая попытка оплаты закончится ошибкой.</p>
              <p>Автопродления и письма о неоплате возобновятся. Сроки подписок не меняются.</p>
            </>
          ) : (
            <>
              <p>Кнопки оплаты исчезнут из кабинетов отелей, автопродления и письма о неоплате остановятся.</p>
              <p>Сроки подписок продолжат действовать: когда срок кончится, отель перейдёт на Free. Продлевайте вручную — «Назначить / продлить» в карточке организации или на странице «Подписки».</p>
            </>
          )}
        </div>
      </Dialog>
    </Card>
  );
}
